import React, { useEffect, useState, useRef } from 'react';
import qodexLogoFull from '../../assets/logos/logo_small.png';
import {
    ChevronDown,
    CreditCard,
    LogOut,
    UsersRound,
    Cog,
    FileText,
    Sun,
    Moon,
} from 'lucide-react';
import {
    Tooltip as MatToolTip,
    Avatar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Button,
    Box
} from "@mui/material";
import { useDispatch, connect } from 'react-redux';
import { userLogOut } from 'redux-store/currentUserSlice';
import { useHistory } from "react-router-dom";
import { OverlayPanel } from 'primereact/overlaypanel';
import { TokenOutlined } from '@mui/icons-material';
import { GitHub } from '@mui/icons-material';
import { RESET_STORE } from "redux-store/store";
import UpgradeDialog from './UpgradeDialog';
import { useTheme } from '../../contexts/ThemeContext';
import { useSnackbar } from '../../contexts/CustomSnackbarContext';
import { syncTestScenariosToGitHub, inviteGitHubUser } from 'redux-store/testScenarioReducers/testScenarioActions';

const AiAgentHeader = ({ selectedProject, handleOpenTab, user, handleSelectClick, selectedOrganisation }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const accountsDialogRef = useRef(null);
    const availableTokens = (selectedProject?.available_tokens || null);
    const { isDarkMode, toggleTheme } = useTheme();
    const { openSnackbar } = useSnackbar();
    const [upgradeDialogVisible, setUpgradeDialogVisible] = useState(false);

    // GitHub sync related state
    const [syncToGitHubResponseModal, setSyncToGitHubResponseModal] = useState(false);
    const [gitHubRepoUrl, setGitHubRepoUrl] = useState(null);
    const [gitHubUserName, setGitHubUserName] = useState(null);
    const [commitMessageDialog, setCommitMessageDialog] = useState(false);
    const [commitMessage, setCommitMessage] = useState("");
    const [gitHubInviteResponse, setGitHubInviteResponse] = useState(null);

    const handleTabNavigation = (tabConfig, tabType) => {
        if (!handleOpenTab || typeof handleOpenTab !== 'function') {
            // Navigate to new page when handleOpenTab is not provided
            history.push(`/${tabType}`);
        } else {
            // Use the provided handleOpenTab function
            handleOpenTab(tabConfig, tabType);
        }
    };

    const formatNumber = (num) => {
        if (num >= 1_000_000_000_000) {
            return { value: +(num / 1_000_000_000_000).toFixed(1), unit: 'T' }; // Trillion
        }
        if (num >= 1_000_000_000) {
            return { value: +(num / 1_000_000_000).toFixed(1), unit: 'B' }; // Billion
        }
        if (num >= 1_000_000) {
            return { value: +(num / 1_000_000).toFixed(1), unit: 'M' }; // Million
        }
        if (num >= 1_000) {
            return { value: +(num / 1_000).toFixed(1), unit: 'K' }; // Thousand
        }
        return { value: num, unit: '' };
    };

    const handleLogout = async () => {
        localStorage.clear();
        await dispatch(userLogOut());
        await dispatch({ type: RESET_STORE })
        history.push('/login', { showEmailSentMessage: false });
    };

    // GitHub sync functions
    const handleSyncToGitHubClick = () => {
        setCommitMessageDialog(true);
    };

    const handleCommitMessageClose = () => {
        setCommitMessageDialog(false);
        setCommitMessage("");
    };

    const handleCommitMessageSubmit = () => {
        syncToGitHub(commitMessage);
        handleCommitMessageClose();
    };

    const syncToGitHub = async (commitMessage) => {
        const response = await dispatch(syncTestScenariosToGitHub({ projectId: selectedProject.id, commitMessage: commitMessage })).unwrap();
        if (response.success) {
            openSnackbar({ message: "Test scenarios are being synced to GitHub.", severity: "success" });
            setSyncToGitHubResponseModal(true);
            setGitHubRepoUrl(response.github_repo_url);
        }
    };

    const handleGitHubInvite = async () => {
        setGitHubInviteResponse(null);
        const response = await dispatch(inviteGitHubUser({ projectId: selectedProject.id, gitHubUserName: gitHubUserName })).unwrap();
        if (response) {
            let errorMessage = null;
            let successMessage = null;
            if (response.error_messages && response.error_messages.length > 0) {
                errorMessage = response.error_messages.join('\n');
            }
            if (response.success_messages && response.success_messages.length > 0) {
                successMessage = response.success_messages.join('\n');
            }
            const finalMessage = [errorMessage, successMessage].filter(Boolean).join('\n\n');
            setGitHubInviteResponse(finalMessage);
            openSnackbar({ message: finalMessage, severity: "info" });
            handleCloseGitHubInviteModal();
        }
    }

    const handleCloseGitHubInviteModal = () => {
        setGitHubUserName(null);
        setSyncToGitHubResponseModal(false);
        setGitHubRepoUrl(null)
    }

    return (
        <>
            <UpgradeDialog
                visible={upgradeDialogVisible}
                onClose={() => setUpgradeDialogVisible(false)}
                availableTokens={availableTokens}
            />
            <header className="w-full bgPrimary p-2 flex justify-between items-center" style={{ borderBottom: "1px solid var(--borderColor)" }}>
                <div className='flex justify-start'>
                    <div className='px-2'>
                        <img
                            src={qodexLogoFull}
                            alt="Logo"
                            className="h-8 object-contain rounded-full bg-white hover:bg-[var(--hover-bg)] custom_border"
                        />
                    </div>
                    <div className='mr-2 ml-2 text-center'>
                        <span className='text-xl !text-[var(--text-secondary)]'>/</span>
                    </div>
                    <div>
                        {selectedProject && (
                            <button
                                onClick={(e) => handleSelectClick(e)}
                                className='flex flex-row items-center py-1 px-2 border-none bgPrimary hover:bg-[var(--hover-bg)] text-capitalize text-[var(--text-primary)] !font-medium rounded-2'>
                                <Avatar
                                    alt="Project"
                                    sx={{ width: 24, height: 24, background: "var(--header-avatar)", color: "var(--text-primary)", border: "1px solid var(--borderColor) !important" }}
                                >
                                    {selectedProject && (
                                        <span className='!text-xs'>
                                            {selectedProject?.name
                                                ? `${selectedProject.name.split(' ')?.[0]?.[0]?.toUpperCase() || ''}${selectedProject.name.split(' ')?.[1]?.[0]?.toUpperCase() || ''}`
                                                : ''}
                                        </span>
                                    )}
                                </Avatar>
                                <div className='flex flex-row'>
                                    <span className='!text-[12px] ml-2'>{selectedProject?.name}</span>
                                    <span className='!text-[12px] ml-2'>
                                        <ChevronDown size={14} color='var(--text-secondary)' />
                                    </span>
                                </div>
                            </button>
                        )}
                    </div>
                </div>
                <div className='flex items-center bgPrimary '>
                    <div className='flex items-center bgPrimary mx-1'>
                        <MatToolTip title={<span className='text-sm'>{availableTokens}</span>} placement='bottom'>
                            <button
                                className='flex items-center py-1 px-2 border-none bgPrimary hover:bg-[var(--hover-bg)] text-capitalize text-[var(--text-primary)] !font-medium rounded-2 cursor-pointer'
                                onClick={() => {
                                    handleTabNavigation(
                                        {
                                            title: "Transactions",
                                            subtitle: "Check Tokens",
                                            type: "transactions",
                                            id: "transactions",
                                            color: "rose",
                                        },
                                        "transactions"
                                    );
                                }}
                            >
                                <Avatar
                                    alt="AI Tokens"
                                    sx={{ width: 24, height: 24, background: "var(--header-avatar)", color: "var(--text-primary)", border: "1px solid var(--borderColor) !important" }}
                                >
                                    <TokenOutlined className='text-[var(--text-primary)]' size={12} />
                                </Avatar>
                                <span className='ml-2 fs12'>AI Tokens:</span>
                                <span className={`ml-2 ${availableTokens > 10000 ? "text-green-600" : "text-red-600"}`}>
                                    {formatNumber(availableTokens).value}
                                    {formatNumber(availableTokens).unit}
                                </span>
                            </button>
                        </MatToolTip>
                    </div>
                    <MatToolTip title="Check Documentation" position="bottom">
                        <button
                            className='flex items-center py-1 px-2 border-none bgPrimary hover:bg-[var(--hover-bg)] text-capitalize text-[var(--text-primary)] !font-medium rounded-2'
                            onClick={() => window.open('https://docs.qodex.ai/introduction', '_blank')}
                        >
                            <Avatar
                                alt="Docs"
                                sx={{ width: 24, height: 24, background: "var(--header-avatar)", color: "var(--text-primary)", border: "1px solid var(--borderColor) !important" }}
                            >
                                <FileText size={14} />
                            </Avatar> <span className='ml-2 fs12'>Docs</span>
                        </button>
                    </MatToolTip>
                    <MatToolTip title="Sync To GitHub" position="bottom">
                        <button
                            className='flex items-center py-1 px-2 border-none bgPrimary hover:bg-[var(--hover-bg)] text-capitalize text-[var(--text-primary)] !font-medium rounded-2'
                            onClick={handleSyncToGitHubClick}
                        >
                            <Avatar
                                alt="Sync To GitHub"
                                sx={{ width: 24, height: 24, background: "var(--header-avatar)", color: "var(--text-primary)", border: "1px solid var(--borderColor) !important" }}
                            >
                                <GitHub size={16} />
                            </Avatar>
                        </button>
                    </MatToolTip>
                    <button
                        className='flex items-center py-1 px-2 border-none bgPrimary hover:bg-[var(--hover-bg)] text-capitalize text-[var(--text-primary)] !font-medium rounded-2'
                        onClick={(e) => accountsDialogRef.current.toggle(e)}
                    >
                        <Avatar
                            alt="Qodex User"
                            sx={{ width: 24, height: 24, background: "var(--header-avatar)", color: "var(--text-primary)", border: "1px solid var(--borderColor) !important" }}
                        >
                            {user && (
                                <span className='fs12'>
                                    {user?.name
                                        ? `${user.name.split(' ')?.[0]?.[0]?.toUpperCase() || ''}${user.name.split(' ')?.[1]?.[0]?.toUpperCase() || ''}`
                                        : 'QU'}
                                </span>
                            )}
                        </Avatar>
                        <ChevronDown className='ml-2' size={12} color='var(--text-secondary)' />
                    </button>
                    <OverlayPanel ref={accountsDialogRef} className="w-52 bgPrimary border !border-[var(--borderColor)]">
                        <div className="mb-2 px-2">
                            <span className='text-[13px] text-[var(--text-primary)]'>{user && user.name ? user.name : 'Qodex User'}</span><br />
                            <span className='text-[12px] text-[var(--text-secondary)] '>{user && user.email ? user.email : ''}</span>
                        </div>
                        <hr className="bgPrimary my-0" />
                        <ul className="w-full !pl-0 list-none mb-0 overflow-y-auto max-h-96 chatscreen-custom-scrollbar  fs13">
                            <li
                                className="flex items-center p-2 text-[var(--text-primary)] hover:bg-[var(--hover-bg)] transition-all cursor-pointer"
                                onClick={() => { window.open("/members", "_blank") }}
                            >
                                <UsersRound size={14} /> <span className='pl-2'>Invite Members</span>
                            </li>
                            <li
                                className="flex items-center p-2 text-[var(--text-primary)] hover:bg-[var(--hover-bg)] transition-all cursor-pointer"
                                onClick={() => { window.open("/transactions", "_blank") }}
                            >
                                <CreditCard size={14} /> <span className='pl-2'>Transactions</span>
                            </li>
                            <li
                                className="flex items-center p-2 text-[var(--text-primary)] hover:bg-[var(--hover-bg)] transition-all cursor-pointer"
                                onClick={() => { window.open("/settings", "_blank") }}
                            >
                                <Cog size={14} /> <span className='pl-2'>Settings</span>
                            </li>
                            <li
                                className="flex items-center p-2 text-[var(--text-primary)] hover:bg-[var(--hover-bg)] transition-all cursor-pointer"
                                onClick={toggleTheme}
                            >
                                {isDarkMode ? (<>
                                    <Sun size={14} /> <span className='pl-2'>Light Mode</span>
                                </>) : (<>
                                    <Moon size={14} /> <span className='pl-2'>Dark Mode</span>
                                </>)}
                            </li>
                        </ul>
                        <hr className="bgPrimary my-0" />
                        <ul className="w-full !pl-0 list-none mb-0 overflow-y-auto max-h-96 chatscreen-custom-scrollbar fs13">
                            <li
                                className="flex items-center p-2 text-[var(--text-primary)] hover:bg-[var(--hover-bg)] transition-all cursor-pointer"
                                onClick={handleLogout}
                            >
                                <LogOut size={14} /> <span className='pl-2'> Log Out </span>
                            </li>
                        </ul>
                    </OverlayPanel>
                </div>
            </header>

            {/* GitHub sync dialogs */}
            <Dialog
                open={syncToGitHubResponseModal}
                onClose={handleCloseGitHubInviteModal}
                aria-labelledby="GitHub Invitation"
            >
                <DialogTitle>Test Scenarios are being synced to GitHub</DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <DialogContentText>
                        To invite a user, please enter their username. Your GitHub Repo URL: <a href={gitHubRepoUrl} target="_blank" rel="noopener noreferrer">
                            {gitHubRepoUrl}
                        </a>
                    </DialogContentText>
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="username"
                            label="GitHub Username"
                            type="text"
                            fullWidth
                            value={gitHubUserName}
                            onChange={(e) => setGitHubUserName(e.target.value)}
                            helperText={"Please enter a GitHub username. To invite multiple users, provide usernames separated by commas (e.g., qodex, support)."}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseGitHubInviteModal}
                        variant="outlined" color="error"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleGitHubInvite}
                        color="primary"
                        variant="contained"
                        disabled={gitHubUserName === null || gitHubUserName === ""}
                    >
                        Invite
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={commitMessageDialog}
                onClose={handleCommitMessageClose}
                aria-labelledby="commit-message-dialog"
            >
                <DialogTitle>Enter Commit Message</DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <DialogContentText>
                        Please enter a commit message for syncing test scenarios to GitHub.
                        {selectedProject?.github_repo_url ? (
                            <>
                                {' '}Your GitHub Repo URL:{' '}
                                <a href={selectedProject.github_repo_url} target="_blank" rel="noopener noreferrer">
                                    {selectedProject.github_repo_url}
                                </a>
                            </>
                        ) : (
                            <>
                                {' '}You are syncing to GitHub for the first time.
                            </>
                        )}
                    </DialogContentText>
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="commit-message"
                            label="Commit Message"
                            type="text"
                            fullWidth
                            multiline
                            rows={4}
                            value={commitMessage}
                            onChange={(e) => setCommitMessage(e.target.value)}
                            helperText="Enter a descriptive commit message for the changes"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCommitMessageClose}
                        variant="outlined" color="error"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCommitMessageSubmit}
                        color="primary"
                        variant="contained"
                        disabled={!commitMessage.trim()}
                    >
                        Sync
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

function mapStateToProps(state, ownProps) {
    return {
        ...ownProps,
        selectedProject: state.common.selectedProject,
        selectedOrganisation: state.common.selectedOrganisation,
        user: state.user.current_user
    }
}

export default connect(mapStateToProps)(AiAgentHeader);